<template>
  <div class="container mx-auto">
    <div
      class="relative grid items-center gap-8 mt-6 mb-12 md:grid-cols-2 lg:mt-8 lg:mb-24"
    >
      <div>
        <h1
          class="mt-4 md:mt-6 lg:mt-8 text-3xl sm:text-4xl lg:text-6xl xl:text-7xl"
        >
          {{ dataLocales.data.title }}
        </h1>
        <p class="mt-4 text-lg md:mt-6 lg:mt-8 md:text-xl text-grow">
          {{ dataLocales.data.description }}
        </p>
        <div class="flex mt-4 md:mt-6 w-fit lg:mt-8 xs:flex-row flex-col gap-4">
          <a
            :href="useRuntimeConfig().public.app_user_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-btn h-12 px-5 font-semibold bg-success hover:bg-successshades text-white whitespace-nowrap transition inline-flex items-center justify-center rounded-full"
          >
            {{ $t("try_our_service") }}
          </a>
          <nuxt-link
            class="text-btn h-12 px-5 font-semibold box-border border text-neutrals-opaque-13 bg-white hover:bg-black/10 whitespace-nowrap transition inline-flex items-center justify-center rounded-full"
           :to="localePath('/contact-us')"
            >{{ $t("contact") }}</nuxt-link
          >
        </div>
      </div>
      <div class="h-full hidden md:flex items-center justify-center">
        <ClientOnly>
          <Vue3Lottie :animationData="marketingAnimation" :height="360" />
        </ClientOnly>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Vue3Lottie } from "vue3-lottie";
import "vue3-lottie/dist/style.css";
import marketingAnimation from "~/assets/animations/creative-3d-animation.json";
import DataLocale from "~~/config/interface/DataLocale";

export default defineComponent({
  components: {
    Vue3Lottie,
  },
  setup() {
    const dataLocales = new DataLocale([
      {
        locale: "en",
        title: "QR Codes in Marketing",
        description:
          "QR codes offer a dynamic way to engage directly with customers or potential clients, surpassing the reach of traditional ads and other media channels.",
      },
      {
        locale: "fr",
        title: "Les codes QR dans le marketing",
        description:
          "Les codes QR offrent un moyen dynamique d'interagir directement avec les clients ou les clients potentiels, dépassant la portée des publicités traditionnelles et autres canaux médiatiques.",
      },
      {
        locale: "it",
        title: "I codici QR nel marketing",
        description:
          "I codici QR offrono un modo dinamico per interagire direttamente con i clienti o i potenziali clienti, superando la portata degli annunci tradizionali e di altri canali mediatici.",
      },
      {
        locale: "de",
        title: "QR-Codes im Marketing",
        description:
          "QR-Codes bieten eine dynamische Möglichkeit, direkt mit Kunden oder potenziellen Kunden in Kontakt zu treten und übertreffen die Reichweite traditioneller Anzeigen und anderer Medienkanäle.",
      },
      {
        locale: "es",
        title: "Códigos QR en Marketing",
        description:
          "Los códigos QR ofrecen una forma dinámica de interactuar directamente con los clientes o clientes potenciales, superando el alcance de los anuncios tradicionales y otros canales de medios.",
      },
      {
        locale: "nl",
        title: "QR-codes in marketing",
        description:
          "QR-codes bieden een dynamische manier om rechtstreeks in contact te komen met klanten of potentiële klanten, en overtreffen het bereik van traditionele advertenties en andere mediakanalen.",
      },
      {
        locale: "pt",
        title: "Códigos QR em Marketing",
        description:
          "Os códigos QR oferecem uma forma dinâmica de se envolver diretamente com clientes ou potenciais clientes, superando o alcance de anúncios tradicionais e outros canais de mídia.",
      },
      {
        locale: "ja",
        title: "マーケティングにおけるQRコード",
        description:
          "QRコードは、従来の広告や他のメディアチャネルのリーチを超えて、顧客や潜在的な顧客と直接関わるための動的な方法を提供します。",
      },
      {
        locale: "zh",
        title: "营销中的二维码",
        description:
          "二维码提供了一种与客户或潜在客户直接互动的动态方式，超越了传统广告和其他媒体渠道的覆盖范围。",
      },
      {
        locale: "vi",
        title: "Mã QR trong Marketing",
        description:
          "Mã QR cung cấp một cách năng động để tương tác trực tiếp với khách hàng hoặc khách hàng tiềm năng, vượt qua phạm vi tiếp cận của quảng cáo truyền thống và các kênh truyền thông khác.",
      },
      {
        locale: "id",
        title: "Kode QR dalam Pemasaran",
        description:
          "Kode QR menawarkan cara dinamis untuk terlibat langsung dengan pelanggan atau calon klien, melampaui jangkauan iklan tradisional dan saluran media lainnya.",
      },
      {
        locale: "tr",
        title: "Pazarlamada QR Kodları",
        description:
          "QR kodları, müşterilerle veya potansiyel müşterilerle doğrudan etkileşim kurmanın dinamik bir yolunu sunar ve geleneksel reklamların ve diğer medya kanallarının erişimini aşar.",
      },
      {
        locale: "hi",
        title: "विपणन में QR कोड",
        description:
          "QR कोड ग्राहकों या संभावित ग्राहकों के साथ सीधे जुड़ने का एक गतिशील तरीका प्रदान करते हैं, जो पारंपरिक विज्ञापनों और अन्य मीडिया चैनलों की पहुंच से अधिक है।",
      },
      {
        locale: "th",
        title: "คิวอาร์โค้ดในการตลาด",
        description:
          "คิวอาร์โค้ดนำเสนอวิธีการที่คล่องตัวในการมีส่วนร่วมโดยตรงกับลูกค้าหรือผู้มีโอกาสเป็นลูกค้า ซึ่งเหนือกว่าการเข้าถึงของโฆษณาแบบดั้งเดิมและช่องทางสื่ออื่นๆ",
      },
    ]);
    return {
      marketingAnimation: ref(marketingAnimation),
      dataLocales,
      localePath:useLocalePath()
    };
  },
});
</script>
